import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";

import qapi from "views_shared/app/javascript/api";
import MessageComposer from "../../my_inbox/MessageComposer";
import { MessagesProvider } from "../../my_inbox/MessagesContext";

// Reply to All (V1's FollowupAllModal behavior): one message to EVERY sent pitch on the
// request — the server fans out, appending to existing conversations and creating the rest.
// Batches over the server's sync cap come back 202 queued; the modal then shows the
// "sending shortly" state instead of closing (V1 parity).
function ReplyAllModal({ show, onHide, request, pitchCount, onReplied }) {
  const [queuedNotice, setQueuedNotice] = useState(null);
  const [sendSlot, setSendSlot] = useState(null);

  const sendOverride = useCallback(
    async ({ body, attachments, videoRequest }) => {
      const result = await qapi.pitchReplies.send(request.id, { body, attachments, videoRequest });
      if (result.error) return result;

      if (result.data?.data?.attributes?.queued) {
        setQueuedNotice(
          `Sending your replies to ${pitchCount} expert${pitchCount === 1 ? "" : "s"} — ` +
            "they'll appear in your inbox shortly.",
        );
      } else {
        onHide();
      }
      onReplied();
      return result;
    },
    [request.id, pitchCount, onHide, onReplied],
  );

  const handleHide = () => {
    setQueuedNotice(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleHide} size="lg" centered contentClassName="pitch-inbox">
      <Modal.Header closeButton>
        <Modal.Title className="paragraph-lg">Reply to All ({pitchCount})</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="alert pi-reply-note">
          <i className="fa-regular fa-circle-info" /> <strong>Note:</strong> Qwoted recommends scheduling a video
          interview to validate expertise.
        </div>
        {queuedNotice ? (
          <>
            <div className="alert alert-success" role="status">
              {queuedNotice}
            </div>
            <p className="small text-secondary mb-2">
              Experts who already received this exact message are skipped automatically — no one gets a duplicate.
            </p>
          </>
        ) : (
          <>
            <div className="row align-items-center mb-3">
              <label htmlFor="pi-reply-all-subject" className="col-2 col-form-label">
                Subject
              </label>
              <div className="col-10">
                <input id="pi-reply-all-subject" className="form-control" value={`Re: ${request.title || ""}`} disabled />
              </div>
            </div>
            <div className="my-inbox pi-reply-composer">
              <MessagesProvider conversationId={null} sendOverride={sendOverride}>
                <MessageComposer sendButtonPortal={sendSlot} />
              </MessagesProvider>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0">
        {queuedNotice ? (
          <>
            <a className="btn btn-outline-secondary" href="/my_inbox_v2">
              View inbox
            </a>
            <button type="button" className="btn btn-primary" onClick={handleHide}>
              Done
            </button>
          </>
        ) : (
          <>
            <div ref={setSendSlot} className="d-inline-flex" />
            <button type="button" className="btn btn-secondary" onClick={handleHide}>
              Close
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

ReplyAllModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  request: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string,
  }).isRequired,
  pitchCount: PropTypes.number.isRequired,
  onReplied: PropTypes.func.isRequired,
};

export default ReplyAllModal;
